import { CommonModule } from '@angular/common';
import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CooperativeTestService, CooperativeMixDto, CooperativeTestDto, CooperativeTestReportDataDto } from '../../services/cooperative-test.service';
import { PrintService } from '../../shared/print/print-dialog.component';

export interface CooperativeTestDialogData {
  projectId: number;
  projectName: string;
  wellName: string;
}

export interface SelectedMixWithTests {
  ufSlurryId: string;
  slurryId: number;
  name: string;
  type: number;
  tests: CooperativeTestDto[];
}

@Component({
  selector: 'app-cooperative-test-request',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule],
  templateUrl: './cooperative-test-request.component.html',
  styleUrls: ['./cooperative-test-request.component.css']
})
export class CooperativeTestRequestComponent {
  @ViewChild('reportContent') reportContent!: ElementRef<HTMLElement>;

  reportHtml = '';
  selectedMixes: SelectedMixWithTests[] = [];
  reportData: CooperativeTestReportDataDto | null = null;

  // Form fields
  requestedDate: string;
  dueDate: string;
  notes: string = '';

  // Inline mix picker state
  showMixDropdown = false;
  isLoadingMixes = false;
  availableMixes = [];
  selectedMixId: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<CooperativeTestRequestComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CooperativeTestDialogData,
    private cooperativeTestService: CooperativeTestService,
    private printService: PrintService
  ) {
    this.requestedDate = this.getTodayDate();
    this.dueDate = this.getTodayDate();
  }


  isSubmitting = false;

  getTodayDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Called by "Add Mix To Report" button — fetches available mixes and
  // reveals the inline dropdown row (GET /api/v1/project-details/available-mixes/{projectId})
  openAddMixRow(): void {
    this.isLoadingMixes = true;
    this.showMixDropdown = true;
    this.selectedMixId = null;

    const alreadySelected = this.selectedMixes.map(m => m.ufSlurryId);

    this.cooperativeTestService.getAvailableMixes(this.data.projectId).subscribe({
      next: (response) => {
        this.availableMixes = (response || []).filter(
          id => !alreadySelected.includes(id)
        );
        // Auto-select the first item by default
        this.selectedMixId = this.availableMixes.length > 0 ? this.availableMixes[0].ufslurryId : null;
        this.isLoadingMixes = false;
      },
      error: (err) => {
        console.error('Error fetching available mixes:', err);
        this.isLoadingMixes = false;
        this.showMixDropdown = false;
      }
    });
  }

  // Called by "Ok" button in the inline row — fetches mix details and adds it
  // (GET /api/v1/project-details/mix/{projectId}/{ufSlurryId})
  confirmMixSelection(): void {
    if (!this.selectedMixId) return;

    const ufSlurryId = this.selectedMixId;
    this.showMixDropdown = false;

    this.cooperativeTestService.getMixByUfSlurryId(this.data.projectId, ufSlurryId).subscribe({
      next: (mixData: CooperativeMixDto) => {
        // Avoid duplicates
        if (!this.selectedMixes.find(m => m.ufSlurryId === mixData.ufSlurryID)) {
          this.selectedMixes.push({
            ufSlurryId: mixData.ufSlurryID,
            slurryId: mixData.slurryId,
            name: mixData.name,
            type: mixData.type,
            tests: mixData.tests
          });
        }
      },
      error: (err) => {
        console.error('Error loading mix details:', err);
        alert('Error loading mix details. Please try again.');
      }
    });
  }

  // Called by "Cancel" button in the inline row
  cancelMixSelection(): void {
    this.showMixDropdown = false;
    this.selectedMixId = null;
    this.availableMixes = [];
  }

  removeMix(ufSlurryId: string): void {
    this.selectedMixes = this.selectedMixes.filter(m => m.ufSlurryId !== ufSlurryId);
  }

  submit(): void {
    this.isSubmitting = true;
    this.selectedMixes = this.selectedMixes.map(mix => ({ ...mix, tests: mix.tests.filter(test => test.isSelected) }));
    this.cooperativeTestService.getCooperativeTestReportData(this.data.projectId, this.selectedMixes).subscribe({
      next: (data) => {
        this.reportData = data;
        this.isSubmitting = false;
        this.reportHtml = this.buildReportHtml();
        setTimeout(() => {
          this.printService.print(this.reportContent.nativeElement, {
            pageSize: 'A4 portrait',
            bodyPadding: '8mm',
          });
        }, 300);
      },
      error: (err) => {
        console.error('Error fetching cooperative test report data:', err);
        this.isSubmitting = false;
      }
    });
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${m}/${d}/${y}`;
  }

  private buildReportHtml(): string {
    const rd = this.reportData;
    const projectName = rd?.projectName || this.data.projectName || '';
    const pipeSize = rd?.pipeSize || '';
    const jobType = rd?.jobType || '';
    const jobSubtype = rd?.jobSubtype || '';
    const wellName = rd?.wellName || this.data.wellName || '';
    const wellLocation = rd?.wellLocation || '';
    const businessUnit = rd?.businessUnit || '';
    const wbs = rd?.wbs || '';
    const serviceCompany = rd?.serviceCompany || '';
    const rigName = rd?.rigName || '';
    const measuredDepth = rd?.measuredDepth ?? '';
    const totalVerticalDepth = rd?.totalVerticalDepth ?? '';
    const bhst = rd?.bhst ?? '';
    const bhct = rd?.bhct ?? '';
    const requestedDate = this.formatDate(this.requestedDate);
    const dueDate = this.formatDate(this.dueDate);
    const notes = this.notes;

    const mdStr = measuredDepth !== '' ? `${measuredDepth}ft` : '';
    const tvdStr = totalVerticalDepth !== '' ? `${totalVerticalDepth}ft` : '';
    const bhstStr = bhst !== '' ? `${bhst}F` : '';
    const bhctStr = bhct !== '' ? `${bhct}F` : '';
    const subheaderParts = [pipeSize ? pipeSize + 'in' : '  ', jobSubtype, jobType].filter(Boolean).join('  ');

    const b = 'border: 1px solid #000;';
    const cell = `${b} padding: 3px 6px; font-size: 13px; font-family: "Times New Roman", Times, serif;`;
    const boldCell = `${cell} font-weight: bold;`;

    const mixBlocks = this.selectedMixes.map(mix => {
      const selectedTests = mix.tests.filter(t => t.isSelected);
      const mixReport = rd?.mixes.find(m => m.name === mix.ufSlurryId);
      const testItems = selectedTests.map(t => `<li style="margin-bottom:2px;">${t.type}</li>`).join('');
      return `
        <table style="width:100%; border-collapse:collapse; margin-top:8px;">
          <tr>
            <td style="${cell}">
              <div style="font-weight:bold;font-size:16px;margin-bottom:4px;">
                Dry Materials
              </div>

              <table style="width:100%; border-collapse:collapse; margin-bottom:8px;">
                <thead>
                  <tr>
                    <th style="text-align:left;padding:2px;">Material Name</th>
                    <th style="text-align:right;padding:2px;">Percent (%)</th>
                    <th style="text-align:right;padding:2px;">Weight(lb)</th>
                    <th style="text-align:right;padding:2px;">Lab Weight(g)</th>
                  </tr>
                </thead>
                <tbody>
                  ${(mixReport?.dryMaterials ?? []).map(m => `
                    <tr>
                      <td style="padding:2px;">${m.name}</td>
                      <td style="padding:2px;text-align:right;">${m.percent.toFixed(3)}%</td>
                      <td style="padding:2px;text-align:right;">${m.weight.toFixed(3)}</td>
                      <td style="padding:2px;text-align:right;">${m.labWeight.toFixed(2)}</td>
                    </tr>
                  `).join('')}

                  <tr>
                    <td style="padding-top:4px;">Total</td>
                    <td style="padding-top:4px;text-align:right;">-</td>
                    <td style="padding-top:4px;text-align:right;">
                      ${(mixReport?.dryMaterials ?? [])
                        .reduce((sum, m) => sum + m.weight, 0)
                        .toFixed(3)}
                    </td>
                    <td style="padding-top:4px;text-align:right;">
                      ${(mixReport?.dryMaterials ?? [])
                        .reduce((sum, m) => sum + m.labWeight, 0)
                        .toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div style="font-weight:bold;font-size:16px;margin-top:8px;margin-bottom:4px;">
                Liquid Materials
              </div>

              <table style="width:100%; border-collapse:collapse; margin-bottom:8px;">
                <thead>
                  <tr>
                    <th style="text-align:left;padding:2px;">Material Name</th>
                    <th style="text-align:right;padding:2px;">Gal/Sk</th>
                    <th style="text-align:right;padding:2px;">Lab Weight(g)</th>
                  </tr>
                </thead>
                <tbody>
                  ${(mixReport?.liquidMaterials ?? []).map(m => `
                    <tr>
                      <td style="padding:2px;">${m.name}</td>
                      <td style="padding:2px;text-align:right;">
                        ${m.volume.toFixed(3)}gal/sk
                      </td>
                      <td style="padding:2px;text-align:right;">
                        ${m.labWeight.toFixed(2)}
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </td>
          </tr>
          <tr>
            <td style="${cell}">
              <strong>Yield:</strong> ${mixReport?.yield?.toFixed(2) ?? '0.00'}ft³/sk
              <br/>
              <strong>Density:</strong> ${mixReport?.density?.toFixed(2) ?? '0.00'}ppg
            </td>
          </tr>
          <tr>
            <td style="${cell}">
              <div style="font-weight:bold; margin-bottom:4px;">Please run the following tests:</div>
              <br/>
              <ul style="margin:0; padding-left:18px; font-size:12px;">
                ${testItems || '<li><em>No tests selected</em></li>'}
              </ul>
            </td>
          </tr>
        </table>`;
    }).join('');

    return `
    
      <div style="font-size: 13px; color: #000;">

        <!-- Header -->
        <table style="width:100%; border-collapse:collapse; border-bottom: 3px solid #000;">
          <tr>
            <td style="vertical-align:middle; font-size:24px; font-weight:bold;"><img src="assets/document_logo.png" alt="Chevron" style="height:40px;"></img></td>
            <td style="padding: 4px 6px; text-align:center; font-size:18px; font-weight:bold;">
              Cooperative Test Request
            </td>
            <td style="padding: 4px 6px; text-align:right; font-size:13px; white-space:nowrap; width:30%;">
              <strong>Project Number: </strong><span style="text-decoration:underline;">${projectName}</span>
            </td>
          </tr>
        </table>

        <!-- Subheader -->
        <table style="width:100%; border-collapse:collapse; border-bottom: 1px solid #000;">
          <tr>
            <td style="text-align:center; font-weight:bold; padding: 3px 6px;">${subheaderParts}</td>
          </tr>
        </table>

        <!-- Info grid -->
        <table style="width:100%; border-collapse:collapse;">
          <tr>
            <td style="${boldCell} width:25%;">Well Name:</td>
            <td style="${cell} width:25%;">${wellName}</td>
            <td style="${boldCell} width:25%;">Date Requested:</td>
            <td style="${cell} width:25%;">${requestedDate}</td>
          </tr>
          <tr>
            <td style="${boldCell}">Service Company:</td>
            <td style="${cell}">${serviceCompany}</td>
            <td style="${boldCell}">Date Results Due:</td>
            <td style="${cell}">${dueDate}</td>
          </tr>
          <tr>
            <td style="${boldCell}">Well Location:</td>
            <td style="${cell}">${wellLocation}</td>
            <td style="${boldCell}">Well WBS:</td>
            <td style="${cell}">${wbs}</td>
          </tr>
          <tr>
            <td style="${boldCell}">OPCO:</td>
            <td style="${cell}">${businessUnit}</td>
          </tr>
          <tr>
            <td style="${boldCell}">Notes:</td>
            <td style="${cell}" colspan="4">${notes}</td>
          </tr>
        </table>

        <!-- Rig grid -->
        <table style="width:100%; border-collapse:collapse;">
          <tr>
            <td style="${cell} width:34%;" rowspan="2">
              <strong>Rig Name:</strong>&nbsp;${rigName}
            </td>
            <td style="${boldCell} width:8%;">MD:</td>
            <td style="${cell} width:25%;">${mdStr}</td>
            <td style="${boldCell} width:8%;">TVD:</td>
            <td style="${cell} width:25%;">${tvdStr}</td>
          </tr>
          <tr>
            <td style="${boldCell}">BHST:</td>
            <td style="${cell}">${bhstStr}</td>
            <td style="${boldCell}">BHCT:</td>
            <td style="${cell}">${bhctStr}</td>
          </tr>
        </table>

        ${mixBlocks}
      </div>`;
  }

  close(): void {
    this.dialogRef.close();
  }
}

